import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode } from 'react';
import type { SaveD2216ResultInput, SaveStructuredResultInput } from '@lims/shared-validation';
import { useTranslation } from 'react-i18next';
import {
  D2216_METHODS,
  MOISTURE_DISPLAY_PLACES,
  MOISTURE_READING_FIELDS,
  evaluateMoistureDetermination,
  formatDecimalForDisplay,
  isD2216Document,
  templateEquipmentRoles,
  type D2216Determination,
  type D2216Document,
  type MoistureCalculation,
} from '@lims/shared-types';
import { ApiError } from '../../api/client.js';
import { attemptsApi, type Attempt } from '../../api/endpoints.js';
import {
  ActionButton,
  Alert,
  DateTimeField,
  MarkCalculated,
  MarkEntered,
  Panel,
  ReadOnlyField,
  Reading,
  SelectField,
  TextAreaField,
  TextField,
  useToast,
  fieldGridClass,
} from '../../components/ui/index.js';
import { useSaveFailureFocus } from '../../components/ui/server-form.js';
import { ErrorNotice } from '../../components/ErrorNotice.js';
import { formatDate, formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { testName } from './status.js';
import {
  BeforeSubmitPanel,
  BenchLayout,
  BenchSection,
  CardStateLabel,
  DeterminationCard,
  LiveResultsPanel,
  SectionStrip,
  type BenchStep,
  type CardState,
  type CheckItem,
  type LiveValue,
  type SectionState,
} from './bench-parts.js';
import { ResultBlock } from './specialized-form/index.js';
import { useSampleTypes } from '../samples/sample-type-fields.js';

/**
 * ASTM D2216 — WATER (MOISTURE) CONTENT OF SOIL AND ROCK BY MASS.
 *
 * The first specialised execution template (decisions D237–D247). Every
 * field, label, unit and display format is traced to the laboratory's
 * validated workbook (`PROJECT_DOCUMENTATION.md` section 15.42). Nothing here
 * applies to any other test: an attempt reaches this form only when it
 * STARTED on a ready catalogue item that names this template.
 *
 * ENTERED READINGS LOOK DIFFERENT FROM CALCULATED VALUES (CLAUDE.md
 * section 8). The calculation shown while typing is a PREVIEW by the same
 * shared function the server runs; the stored values are the server's, and a
 * row shows them once saved unchanged.
 *
 * A FAILED SAVE KEEPS EVERYTHING TYPED. The form's state is replaced only by
 * a successful save's answer, never by an error.
 */

interface RowDraft {
  readonly id: string;
  readonly containerNumber: string;
  readonly containerMass: string;
  readonly containerWetMass: string;
  readonly containerDryMass: string;
}

interface FormDraft {
  readonly methodUsed: string | null;
  readonly ovenTemperature: string;
  readonly ovenStartedAt: string | null;
  readonly nominalMaximumSize: string;
  readonly rows: readonly RowDraft[];
  readonly ambientTemperature: string;
  readonly relativeHumidity: string;
  readonly remarks: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
}

const text = (value: string | null | undefined) => value ?? '';
const orNull = (value: string) => (value.trim() === '' ? null : value);

function draftFrom(attempt: Attempt, document: D2216Document): FormDraft {
  return {
    methodUsed: document.testInformation.methodUsed,
    ovenTemperature: text(document.testInformation.ovenTemperature),
    ovenStartedAt: document.testInformation.ovenStartedAt,
    nominalMaximumSize: text(document.testInformation.nominalMaximumSize),
    rows: document.determinations.map((row) => ({
      id: row.id,
      containerNumber: text(row.containerNumber),
      containerMass: text(row.containerMass),
      containerWetMass: text(row.containerWetMass),
      containerDryMass: text(row.containerDryMass),
    })),
    ambientTemperature: text(document.environment.ambientTemperature),
    relativeHumidity: text(document.environment.relativeHumidity),
    remarks: text(document.remarks),
    startedAt: attempt.generalResult.executionStartedAt,
    completedAt: attempt.generalResult.executionCompletedAt,
  };
}

/** The form as the API takes it: blank is absent, readings exactly as typed. */
function payloadOf(current: FormDraft): SaveD2216ResultInput {
  return {
    executionStartedAt: current.startedAt,
    executionCompletedAt: current.completedAt,
    testInformation: {
      methodUsed: (current.methodUsed as 'A' | 'B' | null) ?? null,
      ovenTemperature: orNull(current.ovenTemperature),
      ovenStartedAt: current.ovenStartedAt,
      nominalMaximumSize: orNull(current.nominalMaximumSize),
    },
    determinations: current.rows.map((row) => ({
      id: row.id,
      containerNumber: orNull(row.containerNumber),
      containerMass: orNull(row.containerMass),
      containerWetMass: orNull(row.containerWetMass),
      containerDryMass: orNull(row.containerDryMass),
    })),
    environment: {
      ambientTemperature: orNull(current.ambientTemperature),
      relativeHumidity: orNull(current.relativeHumidity),
    },
    remarks: orNull(current.remarks),
  };
}

/**
 * What the page's Submit reads: the form's CURRENT values, never the last
 * saved draft (D249). Submit sends them, and the API saves, validates and
 * submits them in one transaction.
 */
export interface StructuredFormHandle {
  readonly currentPayload: () => SaveStructuredResultInput | null;
}

export type D2216FormHandle = StructuredFormHandle;

function newRowId(): string {
  return globalThis.crypto.randomUUID();
}

/** The stored row, when the draft row still holds exactly what was saved. */
function savedRow(row: RowDraft, stored: readonly D2216Determination[]): D2216Determination | null {
  const match = stored.find((candidate) => candidate.id === row.id);
  if (!match) return null;
  const same = MOISTURE_READING_FIELDS.every(
    (field) => (match[field] ?? '') === row[field].trim(),
  );
  return same ? match : null;
}

/** The calculation a row shows: the server's when saved unchanged, otherwise the preview. */
function rowCalculation(row: RowDraft, stored: readonly D2216Determination[]) {
  const saved = savedRow(row, stored);
  const preview = evaluateMoistureDetermination(row);
  const isServer = saved !== null && saved.calculation !== null;
  const calculation: MoistureCalculation | null = isServer ? saved.calculation : preview.calculation;
  return { preview, isServer, calculation };
}

/** Whether the last refusal named a field under one of these prefixes. */
function namedUnder(errors: ReadonlyMap<string, string>, prefixes: readonly string[]): boolean {
  return [...errors.keys()].some((field) => prefixes.some((prefix) => field === prefix || field.startsWith(`${prefix}.`)));
}

/** The live instruments cover every role the template names. */
export function rolesCovered(attempt: Attempt): { readonly roles: readonly string[]; readonly covered: boolean; readonly any: boolean } {
  const roles = templateEquipmentRoles(attempt.resultPath);
  const live = attempt.equipment.filter((use) => !use.removedAt);
  return { roles, covered: roles.every((role) => live.some((use) => use.role === role)), any: live.length > 0 };
}

/** The mark inside a reading's box: a filled square, "entered by a person". */
export const ENTERED_MARK = (
  <span className="inline-flex text-default-600" aria-hidden="true">
    <MarkEntered size={10} />
  </span>
);

export function D2216Panel({
  attempt,
  isEditable,
  onSaved,
  timeZone,
  handle,
  submitError,
  equipment,
  attachments,
  submitAction,
}: {
  readonly attempt: Attempt;
  readonly isEditable: boolean;
  readonly onSaved: (next: Attempt) => Promise<void>;
  readonly timeZone: string | null;
  /** Filled by the panel so the page's Submit can read the current values. */
  readonly handle?: MutableRefObject<D2216FormHandle | null>;
  /** A refused submission: its fields are marked here, and nothing typed is reset. */
  readonly submitError?: unknown;
  /** The page's equipment section, placed in the bench's section order. */
  readonly equipment?: ReactNode;
  /** The page's attachments, placed after the worksheet. */
  readonly attachments?: ReactNode;
  /** The page's Submit for review, placed beside Save draft. */
  readonly submitAction?: ReactNode;
}) {
  const { t } = useTranslation();
  const { show } = useToast();
  const formRef = useRef<HTMLDivElement>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const stored = attempt.structuredResult?.document;
  const document = isD2216Document(stored) ? stored : null;
  const [draft, setDraft] = useState<FormDraft | null>(() =>
    document ? draftFrom(attempt, document) : null,
  );

  // Replaced ONLY when the stored document changes — that is, after a
  // successful save or a reload. An error never resets what was typed.
  const storedStamp = attempt.structuredResult?.updatedAt ?? null;
  useEffect(() => {
    if (document) setDraft(draftFrom(attempt, document));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedStamp]);

  const save = useMutation({
    mutationFn: (current: FormDraft) => attemptsApi.saveStructured(attempt.id, payloadOf(current)),
    onSuccess: async (next) => {
      await onSaved(next);
      show({ tone: 'success', message: t('attempt.draftSaved') });
    },
  });

  // The page's Submit reads what is on screen now.
  if (handle) handle.current = { currentPayload: () => (draft ? payloadOf(draft) : null) };

  // The latest refusal — a save's or a submission's — marks the fields.
  const [lastError, setLastError] = useState<unknown>(null);
  useEffect(() => {
    if (save.error) setLastError(save.error);
  }, [save.error]);
  useEffect(() => {
    if (submitError) setLastError(submitError);
  }, [submitError]);
  useEffect(() => {
    if (save.isSuccess) setLastError(null);
  }, [save.isSuccess]);

  // The first field the server named receives focus; otherwise the summary (D205).
  useSaveFailureFocus(lastError, formRef, summaryRef);

  const fieldErrors = useMemo(() => {
    const errors = new Map<string, string>();
    if (lastError instanceof ApiError) {
      for (const detail of lastError.details) {
        if (detail.field && !errors.has(detail.field)) {
          errors.set(
            detail.field,
            t(`fieldErrors.${detail.code}`, { defaultValue: t('d2216.invalidValue') }),
          );
        }
      }
    }
    return errors;
  }, [lastError, t]);

  if (!document || !draft) {
    return <Alert tone="danger" title={t('d2216.missingDocument')} />;
  }

  const update = (patch: Partial<FormDraft>) => setDraft((current) => (current ? { ...current, ...patch } : current));
  const updateRow = (id: string, patch: Partial<RowDraft>) =>
    update({ rows: draft.rows.map((row) => (row.id === id ? { ...row, ...patch } : row)) });
  const errorOf = (field: string) => fieldErrors.get(field);
  const invalid = (field: string) => (errorOf(field) ? { isInvalid: true, error: errorOf(field)! } : {});

  // What the bench's strip, live results and checklist show — read from the
  // screen, never a rule of the test: the server decides on save and submit.
  const calculations = draft.rows.map((row) => rowCalculation(row, document.determinations));
  const calculatedCount = calculations.filter((entry) => entry.calculation !== null).length;
  const datesRecorded = Boolean(draft.startedAt && draft.completedAt);
  const instruments = rolesCovered(attempt);
  const rowsDone = draft.rows.length > 0 && calculatedCount === draft.rows.length;

  const steps: BenchStep[] = [
    {
      id: 'bench-info',
      label: t('bench.section.info'),
      state: datesRecorded && !namedUnder(fieldErrors, ['testInformation', 'executionStartedAt', 'executionCompletedAt']) ? 'done' : 'todo',
    },
    ...(equipment
      ? [
          {
            id: 'bench-equipment',
            label: t('bench.section.equipment'),
            state: (instruments.roles.length > 0 ? (instruments.covered ? 'done' : 'todo') : instruments.any ? 'done' : 'none') as SectionState,
          },
        ]
      : []),
    {
      id: 'bench-worksheet',
      label: t('bench.section.worksheet'),
      state: rowsDone && !namedUnder(fieldErrors, ['determinations']) ? 'done' : 'todo',
    },
    {
      id: 'bench-environment',
      label: t('bench.section.environment'),
      state: namedUnder(fieldErrors, ['environment', 'remarks'])
        ? 'todo'
        : draft.ambientTemperature.trim() && draft.relativeHumidity.trim()
          ? 'done'
          : 'none',
    },
  ];

  const checks: CheckItem[] = [
    { key: 'dates', ok: datesRecorded, label: t('bench.check.dates') },
    ...(instruments.roles.length > 0 ? [{ key: 'instruments', ok: instruments.covered, label: t('bench.check.instruments') }] : []),
    {
      key: 'rows',
      ok: rowsDone,
      label: t('bench.check.rowsCalculated', { done: calculatedCount, total: draft.rows.length }),
    },
    ...(fieldErrors.size > 0 ? [{ key: 'refused', ok: false, label: t('bench.check.serverNamed', { count: fieldErrors.size }) }] : []),
  ];

  const live: LiveValue[] = calculations.map((entry, index) => ({
    key: draft.rows[index]!.id,
    label: `${t('d2216.determinationNumbered', { number: index + 1 })} · ${t('bench.waterContentShort')}`,
    value: (
      <Reading
        value={entry.calculation ? formatDecimalForDisplay(entry.calculation.waterContentPercent, MOISTURE_DISPLAY_PLACES.waterContent) : null}
        provenance="calculated"
        provenanceLabel={entry.isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel')}
        absentLabel={t('d2216.notCalculated')}
        emphasis
      />
    ),
  }));

  return (
    <BenchLayout
      strip={<SectionStrip steps={steps} />}
      main={
        <>
          <AttemptHeaderPanel attempt={attempt} timeZone={timeZone} />

          <div ref={formRef} className="flex flex-col gap-gutter" data-testid="d2216-form">
            <BenchSection id="bench-info">
              <Panel title={t('d2216.testInformationTitle')} description={t('d2216.testInformationHint')}>
                <div className={fieldGridClass(2)}>
                  <SelectField
                    label={t('d2216.methodUsed')}
                    value={draft.methodUsed}
                    onChange={(value) => update({ methodUsed: value })}
                    placeholder={t('d2216.methodPlaceholder')}
                    isReadOnly={!isEditable}
                    isRequired
                    options={D2216_METHODS.map((method) => ({ value: method, label: t(`d2216.method.${method}`) }))}
                    {...invalid('testInformation.methodUsed')}
                  />
                  <TextField
                    label={t('d2216.ovenTemperature')}
                    value={draft.ovenTemperature}
                    onChange={(value) => update({ ovenTemperature: value })}
                    isReadOnly={!isEditable}
                    textDirection="ltr"
                    endContent={ENTERED_MARK}
                    {...invalid('testInformation.ovenTemperature')}
                  />
                  <DateTimeField
                    label={t('d2216.ovenStartedAt')}
                    value={draft.ovenStartedAt}
                    onChange={(value) => update({ ovenStartedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('testInformation.ovenStartedAt')}
                  />
                  <TextField
                    label={t('d2216.nominalMaximumSize')}
                    description={t('d2216.nominalMaximumSizeHint')}
                    value={draft.nominalMaximumSize}
                    onChange={(value) => update({ nominalMaximumSize: value })}
                    isReadOnly={!isEditable}
                    textDirection="ltr"
                    {...invalid('testInformation.nominalMaximumSize')}
                  />
                  <DateTimeField
                    label={t('d2216.dateTested')}
                    value={draft.startedAt}
                    onChange={(value) => update({ startedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionStartedAt')}
                  />
                  <DateTimeField
                    label={t('attempt.executionCompletedLabel')}
                    value={draft.completedAt}
                    onChange={(value) => update({ completedAt: value })}
                    timeZone={timeZone}
                    isReadOnly={!isEditable}
                    {...invalid('executionCompletedAt')}
                  />
                </div>
              </Panel>
            </BenchSection>

            {equipment ? <BenchSection id="bench-equipment">{equipment}</BenchSection> : null}

            <BenchSection id="bench-worksheet">
              <Panel
                title={t('d2216.determinationsTitle')}
                description={t('d2216.determinationsHint')}
                actions={<ProvenanceLegend />}
              >
                {draft.rows.length === 0 ? (
                  <p className="text-body text-default-600" data-testid="d2216-no-rows">
                    {t('d2216.noDeterminations')}
                  </p>
                ) : (
                  <ol className="flex flex-col gap-stack">
                    {draft.rows.map((row, index) => (
                      <DeterminationRow
                        key={row.id}
                        index={index}
                        row={row}
                        stored={document.determinations}
                        isEditable={isEditable}
                        onChange={(patch) => updateRow(row.id, patch)}
                        onRemove={() => update({ rows: draft.rows.filter((candidate) => candidate.id !== row.id) })}
                        errorOf={(field) => errorOf(`determinations.${index}.${field}`)}
                      />
                    ))}
                  </ol>
                )}
                {errorOf('determinations') ? (
                  <p className="mt-stack text-caption font-medium text-danger-700">{errorOf('determinations')}</p>
                ) : null}
                {isEditable ? (
                  <div className="mt-stack flex flex-wrap items-center gap-inline">
                    <ActionButton
                      priority="secondary"
                      onPress={() =>
                        update({
                          rows: [
                            ...draft.rows,
                            { id: newRowId(), containerNumber: '', containerMass: '', containerWetMass: '', containerDryMass: '' },
                          ],
                        })
                      }
                    >
                      {t('d2216.addDetermination')}
                    </ActionButton>
                  </div>
                ) : null}
              </Panel>
            </BenchSection>

            <BenchSection id="bench-environment">
              <Panel title={t('d2216.environmentTitle')} description={t('d2216.environmentHint')}>
                <div className={fieldGridClass(2)}>
                  <TextField
                    label={t('d2216.ambientTemperature')}
                    value={draft.ambientTemperature}
                    onChange={(value) => update({ ambientTemperature: value })}
                    isReadOnly={!isEditable}
                    textDirection="ltr"
                    endContent={ENTERED_MARK}
                    {...invalid('environment.ambientTemperature')}
                  />
                  <TextField
                    label={t('d2216.relativeHumidity')}
                    value={draft.relativeHumidity}
                    onChange={(value) => update({ relativeHumidity: value })}
                    isReadOnly={!isEditable}
                    textDirection="ltr"
                    endContent={ENTERED_MARK}
                    {...invalid('environment.relativeHumidity')}
                  />
                </div>
                <div className="mt-stack">
                  <TextAreaField
                    label={t('d2216.remarks')}
                    value={draft.remarks}
                    onChange={(value) => update({ remarks: value })}
                    isReadOnly={!isEditable}
                    rows={3}
                    {...invalid('remarks')}
                  />
                </div>
                <p className="mt-stack text-caption text-default-600">{t('attempt.emptyIsAbsent')}</p>
              </Panel>
            </BenchSection>
          </div>

          {attachments}
        </>
      }
      side={
        <>
          <LiveResultsPanel values={live} emptyLabel={t('bench.liveResultsEmpty')} />
          <BeforeSubmitPanel
            checks={checks}
            {...(isEditable
              ? {
                  actions: (
                    <>
                      <ActionButton priority="secondary" isLoading={save.isPending} onPress={() => save.mutate(draft)}>
                        {t('attempt.saveDraft')}
                      </ActionButton>
                      <span className="flex-1" />
                      {submitAction}
                    </>
                  ),
                }
              : {})}
            summary={
              <div ref={summaryRef} tabIndex={-1}>
                {save.error ? (
                  fieldErrors.size > 0 ? (
                    <Alert tone="danger" title={t('d2216.saveRefusedTitle')} description={t('d2216.saveRefusedBody')} />
                  ) : (
                    <ErrorNotice error={save.error} />
                  )
                ) : null}
              </div>
            }
          />
        </>
      }
    />
  );
}

/**
 * The D2216 notation (D328): each formula exactly as the approved labels word
 * it (`d2216.waterMass` and the others, in both languages), and the percent
 * sign. Notation is the same in every language, so it is not a translation;
 * `form-layout.test.tsx` holds it to the approved labels word for word. Shown
 * only — the calculation is the shared function, unchanged.
 */
export const D2216_NOTATION = {
  formula: {
    waterMass: 'Mw = Mcms − Mcds',
    drySoilMass: 'Ms = Mcds − Mc',
    waterContent: 'w = Mw / Ms × 100',
  },
  percent: '%',
} as const;

/** The legend a worksheet carries: the filled mark is typed, the hollow one calculated. */
export function ProvenanceLegend() {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap items-center gap-x-stack gap-y-1 text-caption text-default-700">
      <span className="inline-flex items-center gap-1.5">
        <MarkEntered size={10} />
        {t('bench.legendEntered')}
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-medium bg-calc px-1.5 text-calc-ink">
        <MarkCalculated size={10} />
        {t('bench.legendCalculated')}
      </span>
    </span>
  );
}

function DeterminationRow({
  index,
  row,
  stored,
  isEditable,
  onChange,
  onRemove,
  errorOf,
}: {
  readonly index: number;
  readonly row: RowDraft;
  readonly stored: readonly D2216Determination[];
  readonly isEditable: boolean;
  readonly onChange: (patch: Partial<RowDraft>) => void;
  readonly onRemove: () => void;
  readonly errorOf: (field: string) => string | undefined;
}) {
  const { t } = useTranslation();
  // The server's values when the row is saved unchanged; otherwise a preview.
  const { preview, isServer, calculation } = rowCalculation(row, stored);
  // Problems the preview sees; a field the server already named shows the
  // server's message instead, once.
  const issues = preview.issues.filter(
    (issue) => issue.code !== 'reading_required' && !errorOf(issue.field),
  );
  const field = (name: keyof RowDraft) => {
    const error = errorOf(name);
    return error ? { isInvalid: true, error } : {};
  };
  const shaped = (value: string | undefined, places: number) =>
    formatDecimalForDisplay(value ?? null, places);
  const hasError = MOISTURE_READING_FIELDS.some((name) => errorOf(name)) || Boolean(errorOf('containerNumber'));
  const state: CardState = calculation ? 'calculated' : issues.length > 0 || hasError ? 'check' : 'incomplete';

  return (
    <DeterminationCard
      testId="d2216-row"
      ariaLabel={t('d2216.determinationNumbered', { number: index + 1 })}
      title={t('d2216.determinationNumbered', { number: index + 1 })}
      state={<CardStateLabel state={state} label={t(`bench.card.${state}`)} />}
      {...(isEditable ? { onRemove, removeLabel: t('d2216.removeDetermination') } : {})}
      entered={
        <>
          <TextField
            label={t('d2216.containerNumber')}
            value={row.containerNumber}
            onChange={(value) => onChange({ containerNumber: value })}
            isReadOnly={!isEditable}
            textDirection="ltr"
            {...field('containerNumber')}
          />
          <TextField
            label={t('d2216.containerMass')}
            value={row.containerMass}
            onChange={(value) => onChange({ containerMass: value })}
            isReadOnly={!isEditable}
            textDirection="ltr"
            endContent={ENTERED_MARK}
            {...field('containerMass')}
          />
          <TextField
            label={t('d2216.containerWetMass')}
            value={row.containerWetMass}
            onChange={(value) => onChange({ containerWetMass: value })}
            isReadOnly={!isEditable}
            textDirection="ltr"
            endContent={ENTERED_MARK}
            {...field('containerWetMass')}
          />
          <TextField
            label={t('d2216.containerDryMass')}
            value={row.containerDryMass}
            onChange={(value) => onChange({ containerDryMass: value })}
            isReadOnly={!isEditable}
            textDirection="ltr"
            endContent={ENTERED_MARK}
            {...field('containerDryMass')}
          />
        </>
      }
      calculatedTestId="d2216-calculated"
      calculatedSource={calculation ? (isServer ? 'server' : 'preview') : 'none'}
      calculated={(
        [
          ['waterMass', 'waterMass', MOISTURE_DISPLAY_PLACES.mass, t('d2216.unitGram')],
          ['drySoilMass', 'drySoilMass', MOISTURE_DISPLAY_PLACES.mass, t('d2216.unitGram')],
          ['waterContentPercent', 'waterContent', MOISTURE_DISPLAY_PLACES.waterContent, D2216_NOTATION.percent],
        ] as const
      ).map(([key, name, places, unit]) => (
        // The card's cells (D328): the value's name, the value with its unit,
        // and the approved formula as small secondary text — the same formula
        // the full label reads, only set apart so it no longer crowds the value.
        <ResultBlock
          key={key}
          label={t(`d2216.result.${name}`)}
          formula={D2216_NOTATION.formula[name]}
          testId={`d2216-${key}`}
        >
          <Reading
            value={calculation ? shaped(calculation[key], places) : null}
            unit={unit}
            provenance="calculated"
            provenanceLabel={isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel')}
            absentLabel={t('d2216.notCalculated')}
            emphasis={key === 'waterContentPercent'}
          />
        </ResultBlock>
      ))}
      footer={
        <>
          <p className="text-caption text-default-600">
            {calculation ? (isServer ? t('d2216.calculatedByServer') : t('d2216.previewLabel')) : t('d2216.notCalculatedHint')}
          </p>
          {issues.length > 0 ? (
            <ul className="mt-1 text-caption font-medium text-warning-700" data-testid="d2216-row-issues">
              {issues.map((issue) => (
                <li key={`${issue.field}-${issue.code}`}>
                  {t(`d2216.field.${issue.field}`)}: {t(`fieldErrors.${issue.code}`)}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      }
    />
  );
}


/** The read-only header (D246): from the records, never re-entered. Shared by every specialised form. */
export function AttemptHeaderPanel({ attempt, timeZone }: { readonly attempt: Attempt; readonly timeZone: string | null }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  // The laboratory's own name for the sample's type: a label, never an input to the test.
  const { nameOf: sampleTypeName } = useSampleTypes();
  const header = attempt.header;
  if (!header) return null;
  const notRecorded = t('common.notRecorded');
  const zone = timeZone ? { timezone: timeZone } : {};
  const at = (iso: string | null) => formatDateTime(iso, { locale, ...zone }) ?? notRecorded;
  const day = (iso: string | null) => formatDate(iso, { locale, ...zone }) ?? notRecorded;
  const standard = [header.test.referenceStandard, header.test.standardVersion].filter(Boolean).join(' ');

  const facts: readonly [string, string][] = [
    [t('d2216.header.client'), header.client.name ?? notRecorded],
    [t('d2216.header.phone'), header.client.phone ?? notRecorded],
    [t('d2216.header.project'), header.project?.name ?? notRecorded],
    [t('d2216.header.location'), header.location?.name ?? notRecorded],
    [t('d2216.header.workOrder'), header.workOrder.number],
    [t('d2216.header.workOrderDate'), header.workOrder.requestDate ? day(header.workOrder.requestDate) : notRecorded],
    [t('d2216.header.sampleId'), header.sample.number],
    [t('d2216.header.sampleType'), sampleTypeName(header.sample.sampleType)],
    [t('d2216.header.receipt'), header.receipt?.number ?? notRecorded],
    [t('d2216.header.dateReceived'), at(header.sample.receivedAt)],
    [t('d2216.header.dateSampled'), at(header.sample.sampledAt)],
    [t('d2216.header.source'), header.sample.source ?? notRecorded],
    [t('d2216.header.sampledBy'), header.sample.sampledBy ?? notRecorded],
    [t('d2216.header.condition'), header.sample.condition ?? notRecorded],
    [t('d2216.header.testMethod'), standard || testName(header.test, locale)],
    [t('d2216.header.performedAt'), header.department?.name ?? notRecorded],
    [t('d2216.header.technician'), header.technician.displayName ?? notRecorded],
  ];

  return (
    <Panel title={t('d2216.headerTitle')} description={t('d2216.headerHint')}>
      <div className="grid grid-cols-1 gap-stack sm:grid-cols-2 xl:grid-cols-3" data-testid="d2216-header">
        {facts.map(([label, value]) => (
          <ReadOnlyField key={label} label={label}>
            {value}
          </ReadOnlyField>
        ))}
      </div>
    </Panel>
  );
}
